import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { CYRILLIC_REGEXP } from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
//eslint-disable-next-line max-len
import ValidateRecipientAdditionalDetailsModel from 'components/Templates/models/ValidateRecipientAdditionalDetailsModel';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './RecipientAdditionalDetailsStep.scss';
import { getTemplateStepErrorMessage } from 'components/Templates/utils/getTemplateStepErrorMessage';

const RecipientAdditionalDetailsStep = ({ paymentStore, customerNumber, userAccounts }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      registrationNumber: paymentStore.templateData?.registrationNumber || '',
      country: paymentStore.templateData?.country || '',
      city: paymentStore.templateData?.city || '',
      postCode: paymentStore.templateData?.postCode || '',
      address: paymentStore.templateData?.address || ''
    },
    validationSchema: Yup.object().shape({
      registrationNumber: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      country: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      city: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(50, i18n.getMessage('client.validation.message.maxSize', { size: 50 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      postCode: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(15, i18n.getMessage('client.validation.message.maxSize', { size: 15 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      address: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        )
    }),
    onSubmit: async (values) => {
      const requestData = ValidateRecipientAdditionalDetailsModel({ ...paymentStore.templateData, ...values });

      paymentStore.setTemplateData({ ...paymentStore.templateData, ...values });

      await paymentStore.validateTemplateAdditionalRecipientDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  const allCodes = userAccounts?.flatMap((acct) => acct.countries) ?? [];
  const uniqueCodes = [...new Set(allCodes)];
  const countriesOptions = uniqueCodes.map((code) => ({
    key: code,
    value: i18n.getMessage(`country.${code}`)
  }));

  const handleCountryChange = (name, data) => handleChange({ target: { name, value: data } });

  return (
    <form onSubmit={form.handleSubmit} className={'recipient-additional-details-step'}>
      <div className={'recipient-additional-details-step-inputs-wrapper'}>
        <Input
          className={'recipient-additional-details-step-input'}
          name={'registrationNumber'}
          label={i18n.getMessage('templates.form.registrationNumber.label')}
          placeholder={i18n.getMessage('templates.form.registrationNumber.placeholder')}
          value={values.registrationNumber}
          error={
            errors.registrationNumber || getTemplateStepErrorMessage(i18n, paymentStore.error, 'registrationNumber')
          }
          onChange={handleChange}
          isRequired
        />
        <InputDropDownSearch
          autoComplete={'off'}
          className={'recipient-additional-details-step-input'}
          name={'country'}
          label={i18n.getMessage('templates.form.country.label')}
          placeholder={i18n.getMessage('templates.form.country.placeholder')}
          options={countriesOptions}
          value={values.country}
          error={errors.country || getTemplateStepErrorMessage(i18n, paymentStore.error, 'country')}
          onChange={handleCountryChange}
          isRequired
        />
        <Input
          className={'recipient-additional-details-step-input'}
          name={'city'}
          label={i18n.getMessage('templates.form.city.label')}
          placeholder={i18n.getMessage('templates.form.city.placeholder')}
          value={values.city}
          error={errors.city || getTemplateStepErrorMessage(i18n, paymentStore.error, 'city')}
          onChange={handleChange}
          isRequired
        />
        <Input
          className={'recipient-additional-details-step-input'}
          name={'postCode'}
          label={i18n.getMessage('templates.form.postCode.label')}
          placeholder={i18n.getMessage('templates.form.postCode.placeholder')}
          value={values.postCode}
          error={errors.postCode || getTemplateStepErrorMessage(i18n, paymentStore.error, 'postCode')}
          onChange={handleChange}
          isRequired
        />
        <Input
          className={'recipient-additional-details-step-input address-input'}
          name={'address'}
          label={i18n.getMessage('templates.form.address.label')}
          placeholder={i18n.getMessage('templates.form.address.placeholder')}
          value={values.address}
          error={errors.address || getTemplateStepErrorMessage(i18n, paymentStore.error, 'address')}
          onChange={handleChange}
          isRequired
        />
      </div>
      <Button
        className={'recipient-additional-details-step-next-button'}
        type={'primary'}
        roleType={'submit'}
        onClick={handleSubmit}
        isDisabled={paymentStore.isLoading}
      >
        {i18n.getMessage('templates.form.button.next')}
      </Button>
    </form>
  );
};

RecipientAdditionalDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore
}))(observer(RecipientAdditionalDetailsStep));
